import * as RadPopover from '@radix-ui/react-popover';
import classNames from 'classnames';
import NumberField from 'components/molecules/Fields/Number';
import { NumberFieldProps } from 'components/molecules/Fields/Number/Number';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ChevronDownIconOutline } from 'utils/icons';
import FreeTextInput from '../Form/FreeTextInput';
import FreeTextInputProps from '../Form/FreeTextInput/FreeTextInput.types';
import { Select } from '../Form/Select';
import { SelectProps } from '../Form/Select/select.types';
import PreFilledByAITooltip from '../PreFilledByAITooltip.component';
type Size = 'base' | 'md' | 'sm' | 'xs';
type InputGroupProps = PropsWithChildren<{
  size?: Size;
  disabled?: boolean;
  error?: string;
  containerRef?:
    | React.RefObject<HTMLDivElement | null>
    | ((ref: HTMLDivElement | null) => void);
}>;
type State = {
  isHovered?: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  // hasValue: boolean;
  hasError?: boolean;
};

const outerPaddingClasses = {
  base: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-3', b: 'pb-3' }, // 'px-2.5 py-3',
  md: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-2.5', b: 'pb-2.5' }, //'p-2.5'
  sm: { l: 'pl-[0.5rem]', r: 'pr-[0.5rem]', t: 'pt-2', b: 'pb-2' }, //'p-2',
  xs: { l: 'pl-[0.375rem]', r: 'pr-[0.375rem]', t: 'pt-1.5', b: 'pb-1.5' }, // 'p-1.5',
};

type ContainerOptions = {
  useAIColorScheme?: boolean;
};

const getContainerStateClasses = (
  state: State,
  { useAIColorScheme }: ContainerOptions,
) => {
  const { isHovered, isFocused, isDisabled, hasError } = state;

  const defaultShadow = 'shadow-[0px_1px_2px_0px_rgba(53,26,77,0.03)]';
  if (isDisabled) {
    return classNames(
      'border',
      hasError ? 'border-[#EF4444]' : 'border-primary-100',
    );
  }
  if (hasError) {
    return classNames(
      'relative z-[2] border border-[#EF4444]',
      useAIColorScheme ? '' : 'transition',
      isFocused ? 'ring-2 ring-primary-100' : '',
    );
  }
  if (isFocused) {
    return classNames(
      'relative z-[2] border border-primary ring-2 ring-primary-100',
      useAIColorScheme ? '' : 'transition',
    );
  }

  return classNames(
    defaultShadow,
    useAIColorScheme
      ? 'ai-input-bg-border hover:relative hover:z-[2]'
      : 'bg-white transition hover:bg-primary-50 border border-primary-100',
  );
};

type InputOptions = {
  useAIColorScheme?: boolean;
};

const getInputStateClasses = (
  state: State,
  { useAIColorScheme }: InputOptions,
) => {
  const { isHovered, isFocused, isDisabled } = state;
  const generalClasses =
    'flex-1 font-assistant text-base outline-none leading-[1.5rem]';

  if (isDisabled) {
    return classNames(
      generalClasses,
      'bg-primary-50 placeholder-primary-200 cursor-not-allowed transition',
    );
  }

  if (isFocused) {
    return classNames(
      generalClasses,
      'bg-white placeholder-primary-400 text-primary',
      useAIColorScheme ? '' : 'transition',
    );
  }

  if (isHovered) {
    return classNames(
      generalClasses,
      'placeholder-primary-400 bg-primary-50 text-primary',
      useAIColorScheme ? '' : 'transition',
    );
  }

  return classNames(
    generalClasses,
    'bg-white placeholder-primary-400 text-primary',
    useAIColorScheme ? '' : 'transition',
  );
};
const inputGroupContext = createContext<{
  size?: Size;
  disabled?: boolean;
  error?: string;
}>({});
const Group: FC<InputGroupProps> = (props) => {
  const { size = 'base', containerRef, error, disabled, children } = props;

  const innerContainerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(containerRef, () => innerContainerRef.current);

  return (
    <inputGroupContext.Provider value={{ size, disabled, error }}>
      <div
        ref={innerContainerRef}
        className={classNames('relative flex min-w-0')}
      >
        {children}
      </div>
    </inputGroupContext.Provider>
  );
};
const Text: FC<FreeTextInputProps> = (props) => {
  const contextProps = useContext(inputGroupContext);
  return <FreeTextInput {...contextProps} {...props} />;
};
const Number: FC<NumberFieldProps> = (props) => {
  const contextProps = useContext(inputGroupContext);
  return <NumberField {...contextProps} {...props} />;
};
const Dropdown: FC<
  PropsWithChildren<
    Omit<SelectProps, 'name'> & {
      removeLeftRounding?: boolean;
      removeRightRounding?: boolean;
      dropdownAlign?: RadPopover.PopoverContentProps['align'];
      menuPlacement?: RadPopover.PopoverContentProps['side'];
      wrapperClassName?: string;
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
      aiTooltip?: SelectProps['aiTooltip'];
    }
  >
> = (props) => {
  const {
    removeLeftRounding,
    removeRightRounding,
    dropdownAlign,
    menuPlacement,
    children,
    wrapperClassName,
    onMouseEnter,
    onMouseLeave,
    aiTooltip,
    ...selectProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const contextProps = useContext(inputGroupContext);
  const state = {
    isFocused: isFocused || isOpen,
    isHovered,
    isDisabled: !!contextProps.disabled || !!selectProps.isDisabled,
  };
  const containerStateClasses = getContainerStateClasses(state, {
    useAIColorScheme: aiTooltip?.enabled,
  });
  const buttonStateClasses = getInputStateClasses(state, {
    useAIColorScheme: aiTooltip?.enabled,
  });
  const name = '_' + useId();
  const roundingClasses = {
    'rounded-l-[0.5rem]': !removeLeftRounding,
    'rounded-r-[0.5rem]': !removeRightRounding,
  };
  const [containerElm, setContainerElm] = useState<HTMLDivElement | null>(null);
  return (
    <PreFilledByAITooltip
      enabled={aiTooltip?.enabled}
      description={aiTooltip?.description}
      hide={isFocused}
    >
      <div
        ref={setContainerElm}
        className={classNames(
          wrapperClassName,
          'flex group-dropdown-container',
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className={classNames(
            'flex w-full',
            containerStateClasses,
            roundingClasses,
          )}
        >
          <RadPopover.Root open={isOpen} onOpenChange={setIsOpen}>
            <RadPopover.Trigger
              className={classNames('w-full', roundingClasses)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onBlur={() => setIsFocused(false)}
              onFocus={() => setIsFocused(true)}
              disabled={state.isDisabled}
            >
              <div
                className={classNames(
                  'h-full flex items-center justify-between',
                  buttonStateClasses,
                  Object.values(outerPaddingClasses.base),
                  roundingClasses,
                )}
              >
                {children}
                <ChevronDownIconOutline
                  className={classNames(
                    'h-[1.2rem] w-[1.2rem]',
                    props.isDisabled ? 'text-primary-200' : 'text-primary',
                  )}
                />
              </div>
            </RadPopover.Trigger>
            <RadPopover.Portal container={containerElm}>
              <RadPopover.Content
                align={dropdownAlign || 'start'}
                {...(menuPlacement ? { side: menuPlacement } : {})}
                asChild
              >
                <div className="relative z-10 bg-white py-2 rounded-[0.5rem] ring-1 ring-primary-100 my-1.5 shadow">
                  <Select
                    name={name}
                    themeOptions={{
                      controlClassName: 'mx-2',
                      hideDropdownIndicator: true,
                    }}
                    {...selectProps}
                    classNames={{
                      menu: () => 'mt-2 !static',
                    }}
                    menuIsOpen
                    onChange={(...args) => {
                      selectProps.onChange?.(...args);
                      setIsOpen(false);
                    }}
                  />
                </div>
              </RadPopover.Content>
            </RadPopover.Portal>
          </RadPopover.Root>
        </div>
      </div>
    </PreFilledByAITooltip>
  );
};
const Input = {
  Group,
  Text,
  Number,
  Dropdown,
};
export default Input;
